import React from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Spin, Alert, Typography } from 'antd';
import styled from 'styled-components';
import {ICandidate} from "../../../services/types.ts";
import {CandidateItem} from "../components/CandidateItem.tsx";

const { Text } = Typography;

const ListContainerWrapper = styled.div`
    margin-top: 16px;
    flex: 1;
    min-height: 200px;
    height: calc(100vh - 150px);
    width: 100%;
    overflow: hidden;
`;

const LoadingSpinner = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    height: 100%;
`;

const CenteredText = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 20px;
    text-align: center;
`;

const ITEM_SIZE = 135;

// Define status types explicitly for clarity
type QueryStatus = 'pending' | 'error' | 'success' | 'idle';

interface VirtualizedCandidateListProps {
    candidates: ICandidate[];
    status: QueryStatus;
    error: Error | null;
    isFetchingNextPage: boolean;
    hasNextPage: boolean | undefined;
    itemCount: number; // Total items including potential loading placeholders
    loadMoreItems: () => void;
    isItemLoaded: (index: number) => boolean;
    listRef?: React.RefObject<FixedSizeList>;
    infiniteLoaderRef?: React.RefObject<InfiniteLoader>;
}

const Row: React.FC<ListChildComponentProps<ICandidate[]>> = React.memo(({ index, style, data: candidates }) => {
    const candidate = candidates[index];

    if (!candidate) {
        return (
            <div style={style}>
                <LoadingSpinner>
                    <Spin tip="Lade mehr Kandidaten..." />
                </LoadingSpinner>
            </div>
        );
    }

    // Render actual candidate item
    return (
        <div style={style}>
            <CandidateItem item={candidate} />
        </div>
    );
});
Row.displayName = 'ListRow';


export const VirtualizedCandidateList: React.FC<VirtualizedCandidateListProps> = ({
                                                                                      candidates,
                                                                                      status,
                                                                                      error,
                                                                                      itemCount,
                                                                                      loadMoreItems,
                                                                                      isItemLoaded,
                                                                                      listRef,
                                                                                      infiniteLoaderRef,
                                                                                  }) => {

    if (status === 'pending') {
        return (
            <ListContainerWrapper>
                <LoadingSpinner><Spin size="large" tip="Lade Kandidaten..." /></LoadingSpinner>
            </ListContainerWrapper>
        );
    }

    if (status === 'error') {
        return (
            <ListContainerWrapper>
                <Alert message="Fehler beim Laden" description={error?.message || 'Ein unbekannter Fehler ist aufgetreten.'} type="error" showIcon />
            </ListContainerWrapper>
        );
    }

    if (candidates.length === 0 && status === 'success') {
        return (
            <ListContainerWrapper>
                <CenteredText>
                    <Text>Keine Kandidaten für die aktuellen Filter gefunden.</Text>
                </CenteredText>
            </ListContainerWrapper>
        );
    }


    return (
        <ListContainerWrapper>
            <AutoSizer>
                {({ height, width }) => {
                    // Ensure AutoSizer provides valid dimensions
                    if (width === 0 || height === 0) {
                        return null;
                    }

                    return (
                        <InfiniteLoader
                            ref={infiniteLoaderRef}
                            isItemLoaded={isItemLoaded}
                            itemCount={itemCount}
                            loadMoreItems={loadMoreItems}
                            threshold={5} // How many items away from the end triggers loading
                        >
                            {({ onItemsRendered, ref: infiniteLoaderInnerRef }) => (
                                <FixedSizeList
                                    ref={(el) => {
                                        // Assign ref for InfiniteLoader
                                        if (typeof infiniteLoaderInnerRef === 'function') {
                                            infiniteLoaderInnerRef(el);
                                        }
                                        // Assign ref for potential parent usage
                                        if (listRef && typeof listRef !== 'function') {
                                            (listRef as React.MutableRefObject<FixedSizeList | null>).current = el;
                                        }
                                    }}
                                    height={height}
                                    width={width}
                                    itemSize={ITEM_SIZE}
                                    itemCount={itemCount}
                                    onItemsRendered={onItemsRendered}
                                    // Pass candidates data to the Row component
                                    itemData={candidates}
                                    // Use a stable key, ensure candidate has a unique 'key' or 'id'
                                    itemKey={(index, data) => data[index]?.key ?? data[index]?.id ?? `loading-${index}`}
                                    overscanCount={5} // Render items slightly outside the viewport
                                >
                                    {Row}
                                </FixedSizeList>
                            )}
                        </InfiniteLoader>
                    );
                }}
            </AutoSizer>
        </ListContainerWrapper>
    );
};
